import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import fetchApi from '../utils/api.js';

function AccountGroupForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isEdit = !!id;

    const [accountGroupName, setAccountGroupName] = useState('');
    const [loading, setLoading] = useState(false);
    const [companyId, setCompanyId] = useState('');
    const [companies, setCompanies] = useState([]);
    const user = JSON.parse(sessionStorage.getItem('user'));
    const isSuperAdmin = user?.role === 'SUPER_ADMIN';

    useEffect(() => {
        if (isEdit) {
            if (location.state && location.state.group) {
                const grp = location.state.group;
                setAccountGroupName(grp.accountGroupName);
                const cid = grp.companyId?.id || grp.company_id || grp.companyId;
                setCompanyId(cid ? cid : 'all');
            } else {
                fetchGroup();
            }
        }
    }, [id]);

    useEffect(() => {
        if (isSuperAdmin) {
            fetchCompanies();
        }
    }, [isSuperAdmin]);

    const fetchCompanies = async () => {
        try {
            const result = await fetchApi('/company');
            if (result) {
                setCompanies(Array.isArray(result) ? result : result.companies || []);
            }
        } catch (error) {}
    };

    const fetchGroup = async () => {
        try {
            setLoading(true);
            const result = await fetchApi(`/account-group/${id}`);
            if (result) {
                setAccountGroupName(result.accountGroupName);
                const cid = result.companyId?.id || result.company_id || result.companyId;
                setCompanyId(cid ? cid : 'all');
            }
        } catch (error) {
            console.error('Error fetching group:', error);
            toast.error('Failed to load group details');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmedName = accountGroupName.trim();

        if (!trimmedName) {
            toast.error("Account Group name cannot be empty");
            return;
        }

        try {
            setLoading(true);
            const payload = { accountGroupName: trimmedName };
            if (isSuperAdmin) {
                payload.companyId = companyId;
            }
            
            await fetchApi(isEdit ? `/account-group/${id}` : '/account-group', {
                method: isEdit ? 'PUT' : 'POST',
                body: JSON.stringify(payload)
            });

            const user = JSON.parse(sessionStorage.getItem('user'));
            const rolePrefix = user?.role === 'SUPER_ADMIN' ? '/super-admin' : (user?.role === 'ADMIN' ? '/admin' : '/user');

            toast.success(isEdit ? 'Account Group updated!' : 'Account Group added!');
            navigate(`${rolePrefix}/settings/account-group`);
        } catch (error) {
            console.error('Error saving account group:', error);
            toast.error('Failed to save group');
        } finally {
            setLoading(false);
        }
    };

    const rolePrefix = user?.role === 'SUPER_ADMIN' ? '/super-admin' : (user?.role === 'ADMIN' ? '/admin' : '/user');

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">{isEdit ? 'Update Account Group' : 'Add Account Group'}</h1>
            </div>

            <div style={{ 
                padding: '24px',
                background: '#fff',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                marginTop: '20px'
            }}>
                <form onSubmit={handleSubmit}>
                    <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: '24px',
                        marginBottom: '24px'
                    }}>
                        {isSuperAdmin && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                                    SELECT COMPANY
                                </label>
                                <select
                                    value={companyId}
                                    onChange={(e) => setCompanyId(e.target.value)}
                                    required
                                    style={{ padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                                >
                                    <option value="">Select Company</option>
                                    <option value="all">All</option>
                                    {companies.map(comp => (
                                        <option key={comp._id || comp.id} value={comp._id || comp.id}>
                                            {comp.companyName || comp.name || comp.company_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div style={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px'
                        }}>
                            <label style={{ 
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#374151'
                            }}>
                                NAME
                            </label>
                            <input
                                type="text"
                                style={{ 
                                    padding: '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    outline: 'none',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}
                                placeholder="Enter account group name"
                                value={accountGroupName}
                                onChange={(e) => setAccountGroupName(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '16px',
                        marginTop: '32px',
                        paddingTop: '24px',
                        borderTop: '1px solid #f3f4f6'
                    }}>
                        <button 
                            type="button" 
                            style={{ 
                                backgroundColor: '#ffeff0',
                                color: '#ef4444',
                                padding: '12px 32px',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                fontSize: '15px'
                            }}
                            onClick={() => navigate(`${rolePrefix}/settings/account-group`)}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            style={{ 
                                backgroundColor: '#0b3a54',
                                color: '#fff',
                                padding: '12px 32px',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                fontSize: '15px'
                            }}
                            disabled={loading}
                        >
                            {loading ? "Saving..." : "Submit"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AccountGroupForm;
